/**
 * Vista: Detalle / Crear / Editar Remisión (AMX-VTA-FR-06)
 *
 * Modos:
 *   /ventas/remisiones/:id          → ver/editar
 *   /ventas/remisiones/nueva?...    → crear (lanzado desde Checklist FR-11)
 *
 * La creación viene con query param num_serie para precargar el tractor y el
 * cliente del FR-07. El vendedor se elige en un select (usuarios con persona).
 */

import remisionService from '../services/remision.service.js';
import recepcionService from '../services/recepcion.service.js';
import permisos from '../services/permissions.service.js';
import authService from '../services/auth.service.js';
import api from '../services/api.js';
import modal from '../utils/modal.js';
import toast from '../utils/toast.js';
import router from '../router/router.js';

import { hoyLocal } from '../utils/fechas.js';
import { consultarDanos, avisoDanosHtml } from '../utils/avisoDanos.js';
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmtMoneda = (n, m = 'MXN') => {
  if (n === null || n === undefined || isNaN(Number(n))) return '—';
  return `${m === 'USD' ? 'US$' : '$'} ${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtFecha = (f) => {
  if (!f) return '—';
  const [y, m, d] = String(f).substring(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '—';
};

// Texto legal estándar de condiciones de venta (del formato impreso).
const CONDICIONES_DEFAULT = `El cliente se compromete a cuidar la mercancía que se le entregue y hacerse responsable por cualquier daño ocasionado. El cliente se compromete a devolver a Agromx la mercancía no vendida en condiciones óptimas. El cliente deberá entregar a Agromx la cantidad monetaria descrita en la presente remisión cuando la mercancía sea vendida o no se encuentre en las instalaciones del cliente. El cliente deberá permitir a personal de Agromx realizar revisiones periódicas de la mercancía amparada en la presente remisión. Agromx se reserva el dominio de la mercancía hasta su liquidación de lo contrario la pondrá a disposición de Agromx en un lapso no mayor a 2 días laborales. Si la mercancía fuera vendida por el cliente o no estuviera en sus instalaciones y no entregara el pago monetario descrito en la remisión el cliente se obliga a pagar el 4 % mensual por concepto de interés moratorio.`;

export default {
  data: {
    remision: null,
    isNew: false,
    isReadonly: false,
    signaturePad: null,
    tractor: null,
    cliente: null,
    vendedor: null,
    vendedores: []
  },

  async render(params) {
    const id = params?.id;
    this.data.isNew = !id || id === 'nueva';

    // Query param `num_serie` permite deep-link al form precargando el tractor.
    // El `checklist_documentos_id` ya no se lee de URL: ahora viene del lookup
    // que el backend hace al pasar el gate (devuelve el FR-11 vigente).
    const usp = new URLSearchParams(window.location.search);
    this._numSerie = usp.get('num_serie') || null;
    this._checklistId = null;

    if (this.data.isNew) {
      this.data.remision = null;
      // El view es singleton — resetear isReadonly por si veníamos de una remisión cancelada/completada.
      this.data.isReadonly = false;
    } else {
      try {
        const res = await remisionService.obtenerPorId(id);
        this.data.remision = res?.data || res;
        // Editable solo si: estado=emitida Y no completada (es borrador).
        const estaCompletada = this.data.remision.completada === 1 || this.data.remision.completada === true;
        this.data.isReadonly = this.data.remision.estado !== 'emitida' || estaCompletada;
      } catch (err) {
        return `<div style="padding:2rem;text-align:center;color:var(--danger);">Remisión no encontrada</div>`;
      }
    }

    const r = this.data.remision || {};
    const titulo = this.data.isNew ? 'Nueva Remisión' : `Remisión ${r.numero_remision || ''}`;
    const estadoBadge = !this.data.isNew ? this._renderEstadoBadge(r.estado) : '';

    return `
      <style>
        .rem-grid-2col {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        @media (max-width: 640px) {
          .rem-grid-2col { grid-template-columns: 1fr; }
        }
        /* Tabs estilo Checklist Ensamble */
        .rem-tabs-bar {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          padding: 0;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 1rem;
        }
        .rem-tab {
          padding: 0.7rem 0.5rem;
          border: none;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          background: transparent;
          color: #6b7280;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
        }
        .rem-tab:hover:not(.active) { color: #374151; border-bottom-color: #d1d5db; }
        .rem-tab.active { color: var(--primary); font-weight: 600; border-bottom-color: var(--primary); }
        .rem-tab-panel { display: none; }
        .rem-tab-panel.active { display: block; }
      </style>

      <div class="page-header">
        <div>
          <h1>${titulo} ${estadoBadge}</h1>
        </div>
        <div style="display:flex;gap:0.5rem;">
          <button class="btn btn-secondary" onclick="window.currentView.volver()">← Volver</button>
          ${!this.data.isNew && (r.completada === 1 || r.completada === true) && permisos.canPrint?.('remisiones') !== false ? `
            <button class="btn btn-primary" onclick="window.currentView.imprimir()">🖨️ Imprimir</button>
          ` : ''}
          ${!this.data.isNew && r.estado === 'emitida' && (r.completada === 1 || r.completada === true) && permisos.can?.('remisiones', 'cancelar') ? `
            <button class="btn btn-warning" onclick="window.currentView.cancelar()">⛔ Cancelar</button>
          ` : ''}
        </div>
      </div>

      <form id="remisionForm" onsubmit="event.preventDefault();window.currentView.guardar();">

        ${this.data.isNew ? `
          <!-- Buscador de tractor por num_serie (patrón ChecklistDocumentosDetalle) -->
          <div style="background: white; border: 1px solid var(--gray-200); padding: 1.25rem 1.5rem; border-radius: 8px; margin-bottom: 1rem;">
            <h4 style="margin: 0 0 0.25rem; font-size: 1rem; font-weight: 600;">Buscar por número de serie</h4>
            <p style="margin: 0 0 1rem; color: var(--text-muted); font-size: 0.85rem;">Ingrese el número de serie del tractor para cargar sus datos automáticamente</p>
            <div style="display: flex; gap: 0.75rem; align-items: center;">
              <input type="text" id="busquedaNumSerie" class="form-input" style="flex:1;text-transform:uppercase;"
                placeholder="Ej: FTTM5A64KSM000275"
                value="${esc(this._numSerie || '')}" />
              <button type="button" class="btn btn-primary" onclick="window.currentView.buscarTractor()">Buscar</button>
            </div>
            <div id="tractorResult" style="margin-top: 1rem;"></div>
          </div>
        ` : ''}

        <!-- Form principal: en modo NUEVO arranca oculto hasta que el buscar sea exitoso -->
        <div id="formularioPrincipal" ${this.data.isNew ? 'style="display:none;"' : ''}>

        <!-- Tractor (primero — contexto principal del documento) -->
        <div style="border:1px solid var(--gray-200);border-radius:8px;padding:1.25rem;margin-bottom:1.5rem;">
          ${this._sectionTitle('Datos del Tractor')}
          <div id="tractorBox"><p style="color:var(--gray-500);">Cargando...</p></div>
        </div>

        <!-- Datos de la venta (consolida documento + cliente + vendedor) -->
        <div style="border:1px solid var(--gray-200);border-radius:8px;padding:1.25rem;margin-bottom:1.5rem;">
          ${this._sectionTitle('Datos de la venta')}

          <!-- Documento (fecha, forma de pago — el folio ya está en el header) -->
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:0.75rem;">
            <div>
              ${this._miniLabel('Fecha *')}
              <input type="date" id="fecha_remision" class="form-input" required
                value="${r.fecha_remision ? String(r.fecha_remision).substring(0,10) : hoyLocal()}"
                ${this.data.isReadonly ? 'readonly' : ''} />
            </div>
            <div class="form-group" style="margin-bottom:0;">
              ${this._miniLabel('Forma de pago')}
              ${(() => {
                const fp = (r.forma_pago || 'contado').toLowerCase();
                const label = fp === 'credito' ? 'Crédito' : 'Contado';
                return `
                  <input type="hidden" id="forma_pago" value="${esc(fp)}" />
                  <input type="text" id="forma_pago_display" class="form-input" readonly
                    style="background: var(--gray-50);" value="${label}" />
                `;
              })()}
            </div>
          </div>

          <!-- Cliente -->
          <div style="margin-top:1.25rem;">
            <div id="clienteBox"><p style="color:var(--gray-500);">Cargando...</p></div>
          </div>

          <!-- Distribuidor -->
          <div style="margin-top:1.25rem;">
            <div id="vendedorBox"><p style="color:var(--gray-500);">Cargando...</p></div>
          </div>
        </div>

        <!-- Tabs: Precio / Entrega / Recibe -->
        <div style="border:1px solid var(--gray-200);border-radius:8px;padding:1.25rem;margin-bottom:1.5rem;">
          <div class="rem-tabs-bar">
            <button type="button" class="rem-tab active" data-tab="precio" onclick="window.currentView.showTab('precio')">Precio</button>
            <button type="button" class="rem-tab" data-tab="entrega" onclick="window.currentView.showTab('entrega')">Entrega</button>
            <button type="button" class="rem-tab" data-tab="recibe" onclick="window.currentView.showTab('recibe')">Recibe</button>
          </div>

          <!-- Tab: Precio -->
          <div class="rem-tab-panel active" id="panel-precio">
            <!-- Hidden: cantidad/unidad/concepto/total_letras + montos calculados -->
            <input type="hidden" id="cantidad" value="${r.cantidad ?? 1}" />
            <input type="hidden" id="unidad" value="${esc(r.unidad || 'PZ')}" />
            <input type="hidden" id="concepto" value="${esc(r.concepto || '')}" />
            <input type="hidden" id="total_letras" value="${esc(r.total_letras || '')}" />
            <!-- iva (hidden con monto calculado); descuento se captura directo como monto -->
            <input type="hidden" id="iva" value="${r.iva ?? 0}" />

            <!-- Inputs: 2 cols desktop, 1 col móvil -->
            <div class="rem-grid-2col">
              <div>
                ${this._miniLabel('Moneda *')}
                <select id="moneda" class="form-input" required ${this.data.isReadonly ? 'disabled' : ''}
                  onchange="window.currentView.actualizarTotales()">
                  <option value="MXN" ${(r.moneda || 'MXN') === 'MXN' ? 'selected' : ''}>MXN</option>
                  <option value="USD" ${r.moneda === 'USD' ? 'selected' : ''}>USD</option>
                </select>
              </div>
              <div>
                ${this._miniLabel('Precio unitario *')}
                <input type="number" id="precio_unitario" class="form-input" min="0" step="0.01" required
                  placeholder="0.00"
                  value="${r.precio_unitario ?? ''}" ${this.data.isReadonly ? 'readonly' : ''}
                  oninput="window.currentView.actualizarTotales()" />
              </div>
              <div>
                ${this._miniLabel('Descuento')}
                <input type="number" id="descuento" class="form-input" min="0" step="0.01"
                  placeholder="0.00"
                  value="${Number(r.descuento) > 0 ? r.descuento : ''}"
                  ${this.data.isReadonly ? 'readonly' : ''}
                  oninput="window.currentView.actualizarTotales()" />
                <div id="descuento_warning" style="display:none;font-size:0.75rem;color:#b91c1c;margin-top:0.25rem;"></div>
              </div>
              <div>
                ${this._miniLabel('IVA (%)')}
                <input type="number" id="iva_pct" class="form-input" min="0" max="100" step="0.01"
                  placeholder="0"
                  value="${this._calcInitialIvaPct(r.iva, r.precio_unitario, r.cantidad, r.descuento) || ''}"
                  ${this.data.isReadonly ? 'readonly' : ''}
                  oninput="window.currentView.actualizarTotales()" />
                <div id="iva_warning" style="display:none;font-size:0.75rem;color:#b91c1c;margin-top:0.25rem;"></div>
              </div>
            </div>

            <!-- Resumen: lista compacta + total destacado -->
            <div style="margin-top:1.25rem;max-width:480px;margin-left:auto;">
              <div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--gray-600);margin-bottom:0.5rem;">Resumen</div>
              <div style="background:var(--gray-50);border:1px solid var(--gray-200);border-radius:8px;padding:0.75rem 1rem;">
                <div style="display:flex;justify-content:space-between;align-items:center;padding:0.35rem 0;color:var(--gray-700);">
                  <span>Subtotal</span>
                  <span id="subtotalDisplay" style="font-weight:600;">—</span>
                </div>
                <div style="display:flex;justify-content:space-between;align-items:center;padding:0.35rem 0;color:#b91c1c;">
                  <span>Descuento</span>
                  <span id="descuentoDisplay" style="font-weight:600;">—</span>
                </div>
                <div style="display:flex;justify-content:space-between;align-items:center;padding:0.35rem 0;color:var(--gray-700);">
                  <span>IVA</span>
                  <span id="ivaDisplay" style="font-weight:600;">—</span>
                </div>
              </div>
              <div style="background:#dbeafe;border:1px solid #60a5fa;border-radius:8px;padding:0.85rem 1rem;margin-top:0.5rem;display:flex;justify-content:space-between;align-items:center;">
                <span style="font-size:0.85rem;color:#1e40af;text-transform:uppercase;letter-spacing:0.5px;font-weight:700;">Total</span>
                <span id="totalDisplay" style="font-weight:800;font-size:1.5rem;color:#1e40af;">—</span>
              </div>
            </div>
          </div>

          <!-- Tab: Entrega -->
          <div class="rem-tab-panel" id="panel-entrega">
            <div class="form-group">
              <label>Lugar de entrega</label>
              <textarea id="lugar_entrega" class="form-control" rows="3"
                ${this.data.isReadonly ? 'readonly' : ''}>${esc(r.lugar_entrega || '')}</textarea>
            </div>
            <div class="form-group">
              <label>Observaciones</label>
              <textarea id="observaciones" class="form-control" rows="3"
                ${this.data.isReadonly ? 'readonly' : ''}>${esc(r.observaciones || '')}</textarea>
            </div>
          </div>

          <!-- Tab: Recibe -->
          <div class="rem-tab-panel" id="panel-recibe">
            <!-- Hidden: la fecha se auto-asigna al firmar (o queda la guardada en edición) -->
            <input type="hidden" id="recibe_fecha"
              value="${r.recibe_fecha ? String(r.recibe_fecha).substring(0,10) : ''}" />
            <div class="form-group" style="margin-bottom:1rem;">
              <label>Nombre de quien recibe</label>
              <input type="text" id="recibe_nombre" class="form-control" maxlength="200"
                value="${esc(r.recibe_nombre || '')}" ${this.data.isReadonly ? 'readonly' : ''} />
            </div>
            <div id="firmaContainer">
              <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
                <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
                  <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">Recibe</span>
                </div>
                ${r.recibe_firma_url ? `
                  <div style="padding: 1.5rem; text-align: center;">
                    <img src="${esc(r.recibe_firma_url)}" style="width: 100%; max-height: 120px; object-fit: contain;" onerror="this.style.display='none'" />
                  </div>
                  ${r.recibe_fecha ? `<div id="firmaFechaInfo" style="padding: 0 1rem 0.5rem; text-align: center; font-size: 0.75rem; color: var(--gray-500);">Firmado el ${fmtFecha(r.recibe_fecha)}</div>` : ''}
                  ${!this.data.isReadonly ? `
                    <div style="padding: 0 1rem 0.75rem; text-align: center;">
                      <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.currentView.cambiarFirma()">Cambiar firma</a>
                    </div>
                  ` : ''}
                ` : `
                  ${!this.data.isReadonly ? `
                    <div style="position: relative;">
                      <canvas id="firmaCanvas" style="width:100%;height:160px;display:block;"></canvas>
                    </div>
                    <div style="padding: 0.5rem 1rem 0.75rem; text-align: center; border-top: 1px solid var(--gray-200);">
                      <span id="firmaFechaInfo" style="font-size: 0.75rem; color: var(--gray-500);"></span>
                      <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary); margin-left: 1rem;" onclick="window.currentView.limpiarFirma()">Limpiar</a>
                    </div>
                  ` : '<p style="text-align:center;padding:1.5rem;color:var(--gray-500);">Sin firma</p>'}
                `}
              </div>
            </div>
          </div>
        </div>

        <!-- Condiciones de venta: texto legal estándar, solo se muestra en el formato impreso -->
        <input type="hidden" id="condiciones_venta" value="${esc(r.condiciones_venta || (this.data.isNew ? CONDICIONES_DEFAULT : ''))}" />

        ${!this.data.isReadonly ? `
          <!-- Indicador de cambios sin guardar -->
          <div id="unsavedIndicator" style="display: none; background: var(--warning); color: white; padding: 0.5rem 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center;">
            Hay cambios sin guardar
          </div>
          <div style="display:flex;gap:0.5rem;justify-content:flex-end;margin-bottom:2rem;flex-wrap:wrap;">
            <button type="button" class="btn btn-secondary" onclick="window.currentView.volver()">Cancelar</button>
            <button type="submit" class="btn btn-primary">${this.data.isNew ? 'Crear remisión' : 'Guardar cambios'}</button>
            ${!this.data.isNew ? `
              <button type="button" class="btn btn-success" onclick="window.currentView.completar()">Completar</button>
            ` : ''}
          </div>
        ` : ''}

        </div><!-- /#formularioPrincipal -->
      </form>
    `;
  },

  _sectionTitle(title) {
    return `<h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">${title}<span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>`;
  },

  _miniLabel(text) {
    return `<label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">${text}</label>`;
  },

  _miniReadonlyInput(value) {
    return `<input type="text" class="form-input" readonly style="background: var(--gray-50);" value="${esc(value || '')}" />`;
  },

  _renderEstadoBadge(estado) {
    const map = {
      emitida: { c: '#065f46', bg: '#d1fae5', label: 'EMITIDA' },
      cancelada: { c: '#991b1b', bg: '#fee2e2', label: 'CANCELADA' },
      devuelta: { c: '#92400e', bg: '#fef3c7', label: 'DEVUELTA' }
    };
    const b = map[estado] || { c: '#374151', bg: '#e5e7eb', label: estado };
    return `<span style="font-size:0.7rem;padding:0.2rem 0.5rem;border-radius:4px;background:${b.bg};color:${b.c};vertical-align:middle;margin-left:0.5rem;">${b.label}</span>`;
  },

  async afterRender(params) {
    if (!permisos.canView('remisiones')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;

    // El view es singleton — limpiar referencias del render previo para evitar
    // que un signaturePad/firmaViejaABorrar viejo se reutilicen en otra remisión.
    this.data.signaturePad = null;
    this._firmaViejaABorrar = null;

    // Catálogo de vendedores (usuarios con persona) para el select de la remisión.
    await this._cargarVendedores();

    if (this.data.isNew) {
      // Listeners del buscador: Enter dispara, modificar oculta el form de nuevo.
      const nsInput = document.getElementById('busquedaNumSerie');
      if (nsInput) {
        nsInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.buscarTractor();
          }
        });
        nsInput.addEventListener('input', () => {
          // Cualquier edición del num_serie invalida el resultado previo,
          // sea el form precargado (búsqueda OK) o un banner de error
          // (REMISION_ACTIVA_EXISTENTE, CHECKLIST_ENTREGA_REQUERIDO, etc.).
          // El usuario debe darle Buscar de nuevo para validar el nuevo num_serie.
          this.data.tractor = null;
          const fp = document.getElementById('formularioPrincipal');
          if (fp) fp.style.display = 'none';
          const result = document.getElementById('tractorResult');
          if (result) result.innerHTML = '';
        });
      }
      // Si la URL vino con ?num_serie=X (deep link), auto-disparamos el buscar.
      if (this._numSerie) {
        await this.buscarTractor();
      }
    } else {
      // Modo ver/editar — usar datos ya cargados
      this._renderTractorBoxFromRemision();
      this._renderClienteBox(this.data.remision);
      this._renderVendedorBox(this.data.remision);
    }

    this.actualizarTotales();
    if (!this.data.remision?.recibe_firma_url && !this.data.isReadonly) {
      await this._initSignaturePad();
    }

    // Detectar cambios sin guardar (delay para ignorar cambios del render inicial)
    if (!this.data.isReadonly) {
      setTimeout(() => {
        const editArea = document.getElementById('content');
        if (!editArea) return;
        editArea.querySelectorAll('input, select, textarea').forEach(el => {
          el.addEventListener('change', () => this._showUnsaved());
          el.addEventListener('input', () => this._showUnsaved());
        });
      }, 300);
    }
  },

  async buscarTractor() {
    const input = document.getElementById('busquedaNumSerie');
    if (!input) return;
    const numSerie = (input.value || '').trim().toUpperCase();
    if (!numSerie) { toast.warning('Captura el número de serie'); return; }
    this._numSerie = numSerie;
    input.value = numSerie;
    const resultDiv = document.getElementById('tractorResult');
    const fp = document.getElementById('formularioPrincipal');
    if (resultDiv) resultDiv.innerHTML = '<p style="color:var(--gray-600);">Buscando...</p>';

    // Una sola llamada: el lookup del backend aplica el gate completo
    // (tractor existe + sin remisión activa + FR-11 vigente). Cada error trae
    // `code` semántico que mapeamos a un mensaje + link al módulo correcto.
    try {
      await this._cargarTractorYRelacionados(numSerie);
      // C-323c: aviso visible si la unidad tiene daños por corregir (no impide registrar la remisión)
      if (resultDiv) resultDiv.innerHTML = avisoDanosHtml(await consultarDanos(numSerie), { bloquea: false });
      if (fp) fp.style.display = 'block';
      // Re-aplicar listeners de "cambios sin guardar" sobre el form ahora visible
      setTimeout(() => {
        const editArea = document.getElementById('content');
        if (!editArea) return;
        editArea.querySelectorAll('input, select, textarea').forEach(el => {
          if (el.dataset.unsavedBound === '1') return;
          el.dataset.unsavedBound = '1';
          el.addEventListener('change', () => this._showUnsaved());
          el.addEventListener('input', () => this._showUnsaved());
        });
      }, 200);
    } catch (err) {
      if (fp) fp.style.display = 'none';
      this._renderLookupError(err, resultDiv, numSerie);
    }
  },

  _renderLookupError(err, resultDiv, numSerie) {
    if (!resultDiv) return;
    const code = err?.code;
    const details = err?.details || {};
    const msg = err?.message || 'No se pudo cargar el tractor';

    const tplAviso = (titulo, sub, linkHtml, color = 'warning') => {
      const styles = color === 'danger'
        ? 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;'
        : 'background:#fef3c7;color:#92400e;border:1px solid #fbbf24;';
      return `
        <div style="padding:0.75rem 1rem;${styles}border-radius:8px;">
          <strong>${esc(titulo)}</strong><br/>
          <span style="font-size:0.85rem;">${esc(sub)}</span>
          ${linkHtml ? `<div style="margin-top:0.5rem;">${linkHtml}</div>` : ''}
        </div>
      `;
    };

    if (code === 'TRACTOR_NO_ENCONTRADO') {
      resultDiv.innerHTML = tplAviso('No se encontró tractor con ese número de serie', '', '', 'danger');
      return;
    }

    if (code === 'REMISION_ACTIVA_EXISTENTE') {
      const rem = details.remision || {};
      resultDiv.innerHTML = tplAviso(
        `Ya existe una remisión ${rem.pendiente ? 'pendiente (borrador)' : 'completada'} para este tractor`,
        'Cancélala o elimínala antes de crear otra.',
        ''
      );
      return;
    }

    if (code === 'CHECKLIST_ENTREGA_REQUERIDO') {
      resultDiv.innerHTML = tplAviso(
        'Falta el Checklist de Entrega',
        'Primero realiza el checklist de entrega para este tractor.',
        ''
      );
      return;
    }

    if (code === 'CHECKLIST_ENTREGA_INCOMPLETO') {
      resultDiv.innerHTML = tplAviso(
        'El Checklist de Entrega no está completado',
        '',
        ''
      );
      return;
    }

    if (code === 'CHECKLIST_ENTREGA_QUEMADO_POR_DEVOLUCION') {
      resultDiv.innerHTML = tplAviso(
        'El Checklist anterior corresponde a un ciclo previo',
        'Este tractor fue devuelto. Realiza un nuevo Checklist de Entrega para el siguiente comprador.',
        `<a href="javascript:void(0)" onclick="window.currentView._irAChecklistNuevo('${esc(numSerie)}')" style="color:#1e40af;font-weight:600;">→ Crear nuevo Checklist</a>`
      );
      return;
    }

    // Fallback genérico (error inesperado, sin code reconocido)
    resultDiv.innerHTML = tplAviso(msg, '', '', 'danger');
  },

  _irARemision(id) {
    if (id) router.navigate(`/ventas/remisiones/${id}`);
  },

  _irAChecklistNuevo(numSerie) {
    const qs = numSerie ? `?num_serie=${encodeURIComponent(numSerie)}` : '';
    router.navigate(`/ventas/checklist-entrega/nuevo${qs}`);
  },

  _irAChecklistDetalle(id) {
    if (id) router.navigate(`/ventas/checklist-entrega/${id}`);
  },

  _showUnsaved() {
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  _hideUnsaved() {
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'none';
  },

  async _cargarTractorYRelacionados(numSerie) {
    // Lookup self-contained: una sola llamada al endpoint del módulo Remisiones,
    // controlado por `remisiones:crear`. El backend aplica el gate (tractor +
    // sin remisión activa + FR-11 vigente) y devuelve { tractor, checklist,
    // entrega, cliente, vendedor } listo para precargar el form.
    // Cualquier error con `code` se propaga al catch de buscarTractor().
    const res = await remisionService.lookupTractor(numSerie);
    const data = res?.data || res;

    this.data.tractor = data.tractor;
    this._renderTractorBox();

    if (data.cliente) {
      this._cliente_id = data.cliente.id;
      this.data.cliente = data.cliente;
    }
    // El vendedor ya NO se hereda del FR-07: se elige en el select de la remisión.
    this._renderClienteBox();
    this._renderVendedorBox();

    // Heredar forma_pago (tipo_venta) y vincular el FR-11 que pasó el gate.
    // El backend ya validó que existe y está completo; usamos sus datos directo.
    if (data.checklist?.id) {
      this._checklistId = data.checklist.id;
    }
    if (data.checklist?.tipo_venta) {
      const fp = String(data.checklist.tipo_venta).toLowerCase();
      if (fp === 'credito' || fp === 'contado') {
        const hidden = document.getElementById('forma_pago');
        const display = document.getElementById('forma_pago_display');
        if (hidden) hidden.value = fp;
        if (display) display.value = fp === 'credito' ? 'Crédito' : 'Contado';
      }
    }

    this._prefillFromContext();
  },

  _renderTractorBox() {
    const t = this.data.tractor;
    const box = document.getElementById('tractorBox');
    if (!box) return;
    if (!t) { box.innerHTML = '<p style="color:var(--gray-500);">Sin datos.</p>'; return; }
    box.innerHTML = `
      <div class="rem-grid-2col">
        <div>${this._miniLabel('Núm. serie')}${this._miniReadonlyInput(t.num_serie)}</div>
        <div>${this._miniLabel('Modelo')}${this._miniReadonlyInput(t.modelo)}</div>
        <div>${this._miniLabel('Marca')}${this._miniReadonlyInput(t.marca)}</div>
      </div>
    `;
  },

  _renderTractorBoxFromRemision() {
    const r = this.data.remision;
    const box = document.getElementById('tractorBox');
    if (!box) return;
    box.innerHTML = `
      <div class="rem-grid-2col">
        <div>${this._miniLabel('Núm. serie')}${this._miniReadonlyInput(r.num_serie)}</div>
        <div>${this._miniLabel('Modelo')}${this._miniReadonlyInput(r.tractor_modelo || r.modelo)}</div>
        <div>${this._miniLabel('Marca')}${this._miniReadonlyInput(r.tractor_marca || r.marca)}</div>
      </div>
    `;
  },

  _renderClienteBox(remision) {
    const box = document.getElementById('clienteBox');
    if (!box) return;
    const c = remision
      ? { id: remision.cliente_id, nombre_completo: remision.cliente_nombre, rfc: remision.cliente_rfc, direccion: remision.cliente_direccion }
      : this.data.cliente;
    if (!c || !c.id) {
      box.innerHTML = `
        <p style="color:var(--warning);font-weight:600;">⚠️ No hay cliente asignado al tractor.</p>
        <p style="font-size:0.85rem;color:var(--gray-600);margin-top:0.5rem;">El cliente se establece en la <strong>Entrega de Tractor (FR-07)</strong>. Completa ese formato primero.</p>
      `;
      return;
    }
    box.innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0.75rem;">
        <div>${this._miniLabel('Cliente')}${this._miniReadonlyInput(c.nombre_completo)}</div>
        <div>${this._miniLabel('RFC')}${this._miniReadonlyInput(c.rfc)}</div>
      </div>
    `;
  },

  async _cargarVendedores() {
    try {
      const resp = await api.get('/usuarios/responsables').catch(() => ({ data: [] }));
      const todos = Array.isArray(resp.data) ? resp.data : (resp.data?.usuarios || []);
      this.data.vendedores = todos
        .filter(u => u.persona_id)
        .map(u => ({ id: u.persona_id, nombre_completo: u.persona_nombre_completo || u.nombre_completo }));
    } catch (e) {
      this.data.vendedores = [];
    }
  },

  _renderVendedorBox(remision) {
    const box = document.getElementById('vendedorBox');
    if (!box) return;
    const selectedId = remision ? remision.vendedor_id : (this._vendedor_id || null);
    const vendedores = this.data.vendedores || [];

    // En modo solo-lectura mostramos el nombre, no el select.
    if (this.data.isReadonly) {
      const nombre = remision
        ? remision.vendedor_nombre
        : (vendedores.find(x => String(x.id) === String(selectedId))?.nombre_completo || '');
      box.innerHTML = `<div>${this._miniLabel('Vendedor')}${this._miniReadonlyInput(nombre)}</div>`;
      return;
    }

    const opts = ['<option value="">— Selecciona vendedor —</option>']
      .concat(vendedores.map(v =>
        `<option value="${v.id}" ${String(v.id) === String(selectedId) ? 'selected' : ''}>${esc(v.nombre_completo)}</option>`
      )).join('');
    box.innerHTML = `
      <div>${this._miniLabel('Vendedor')}
        <select id="vendedor_id" class="form-input">${opts}</select>
      </div>
    `;
  },

  _prefillFromContext() {
    const t = this.data.tractor;
    if (t) {
      const concepto = `TRACTOR ${t.marca || ''} MODELO: ${t.modelo || ''}`.trim();
      const conceptoEl = document.getElementById('concepto');
      if (conceptoEl && !conceptoEl.value) conceptoEl.value = concepto;
    }
    if (this._lugar_entrega_default) {
      const le = document.getElementById('lugar_entrega');
      if (le && !le.value) le.value = this._lugar_entrega_default;
    }
  },

  showTab(tab) {
    document.querySelectorAll('.rem-tab').forEach(b => {
      b.classList.toggle('active', b.dataset.tab === tab);
    });
    document.querySelectorAll('.rem-tab-panel').forEach(p => {
      p.classList.toggle('active', p.id === `panel-${tab}`);
    });
    // El canvas del SignaturePad debe medirse cuando el tab está visible (offsetWidth>0).
    // Si el pad aún no existe o tiene dimensiones inválidas, lo (re)inicializamos.
    if (tab === 'recibe' && !this.data.remision?.recibe_firma_url && !this.data.isReadonly) {
      const canvas = document.getElementById('firmaCanvas');
      if (canvas && (!this.data.signaturePad || canvas.width === 0)) {
        this._initSignaturePad();
      }
    }
  },

  /**
   * Si una remisión guardó IVA como monto, calcula el porcentaje equivalente
   * sobre la base imponible (subtotal − descuento). Si no hay datos, regresa 0.
   */
  _calcInitialIvaPct(monto_iva, precio_unitario, cantidad, descuento_monto) {
    const m = Number(monto_iva) || 0;
    if (m === 0) return 0;
    const subtotal = (Number(cantidad) || 1) * (Number(precio_unitario) || 0);
    const base = Math.max(0, subtotal - (Number(descuento_monto) || 0));
    if (base <= 0) return 0;
    return Number(((m / base) * 100).toFixed(2));
  },

  /**
   * Calcula totales y actualiza UI. Descuento se captura como monto directo;
   * IVA se captura como %, se calcula el monto y se guarda en hidden input.
   * Si no hay precio_unitario válido, todo queda en 0 (se ignora descuento/iva
   * porque sin base no tiene sentido aplicarlos).
   */
  actualizarTotales() {
    const cantidad = parseFloat(document.getElementById('cantidad')?.value) || 0;
    const precio = parseFloat(document.getElementById('precio_unitario')?.value) || 0;
    const moneda = document.getElementById('moneda')?.value || 'MXN';

    const subtotal = cantidad * precio;
    let descMonto = 0;
    let ivaMonto = 0;
    let total = 0;

    // Reset visual del descuento e IVA (pudo haberse marcado en error antes)
    const descInputEl = document.getElementById('descuento');
    const descWarning = document.getElementById('descuento_warning');
    const ivaInputEl = document.getElementById('iva_pct');
    const ivaWarning = document.getElementById('iva_warning');
    if (descInputEl) descInputEl.style.borderColor = '';
    if (descWarning) { descWarning.style.display = 'none'; descWarning.textContent = ''; }
    if (ivaInputEl) ivaInputEl.style.borderColor = '';
    if (ivaWarning) { ivaWarning.style.display = 'none'; ivaWarning.textContent = ''; }

    if (subtotal > 0) {
      // Solo aplicamos descuento e IVA si hay subtotal (precio capturado).
      const descInput = parseFloat(descInputEl?.value) || 0;
      // Tope: el descuento no puede exceder el subtotal.
      descMonto = Math.min(descInput, subtotal);

      // Warning si el descuento tipeado supera el subtotal
      if (descInput > subtotal && descInputEl && descWarning) {
        descInputEl.style.borderColor = '#b91c1c';
        descWarning.textContent = `⚠️ El descuento excede el subtotal. Se aplica el máximo (${fmtMoneda(subtotal, moneda)}).`;
        descWarning.style.display = 'block';
      }

      const base = subtotal - descMonto;
      const ivaPct = parseFloat(ivaInputEl?.value) || 0;
      ivaMonto = base * (ivaPct / 100);
      total = base + ivaMonto;

      // Warning si capturó IVA% pero la base es 0 (descuento iguala/excede subtotal)
      if (ivaPct > 0 && base === 0 && ivaInputEl && ivaWarning) {
        ivaInputEl.style.borderColor = '#b91c1c';
        ivaWarning.textContent = `⚠️ El descuento iguala/excede el subtotal. La base es $0, por lo que el IVA no aplica.`;
        ivaWarning.style.display = 'block';
      }
    }

    // Hidden con el monto de IVA calculado (lo que se manda al backend)
    const ivaHidden = document.getElementById('iva');
    if (ivaHidden) ivaHidden.value = ivaMonto.toFixed(2);

    const set = (id, val, prefix = '') => {
      const el = document.getElementById(id);
      if (el) el.textContent = (prefix && val > 0 ? prefix : '') + fmtMoneda(val, moneda);
    };
    set('subtotalDisplay', subtotal);
    set('descuentoDisplay', descMonto, '−');
    set('ivaDisplay', ivaMonto);
    set('totalDisplay', total);
  },

  async _initSignaturePad() {
    if (!window.SignaturePad) {
      try {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
          s.onload = res; s.onerror = () => rej(new Error('No se pudo cargar SignaturePad'));
          document.head.appendChild(s);
        });
      } catch (e) { toast.error('Error cargando librería de firmas'); return; }
    }
    setTimeout(() => {
      const canvas = document.getElementById('firmaCanvas');
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      this.data.signaturePad = new SignaturePad(canvas, { backgroundColor: 'rgb(255,255,255)', penColor: 'rgb(0,0,0)' });
      // Al firmar, auto-asignar fecha = hoy y mostrar info text
      this.data.signaturePad.addEventListener('endStroke', () => this._onFirmaStroke());
    }, 50);
  },

  _onFirmaStroke() {
    if (this.data.signaturePad?.isEmpty()) return;
    const hidden = document.getElementById('recibe_fecha');
    const info = document.getElementById('firmaFechaInfo');
    if (!hidden) return;
    // Solo asigna fecha la primera vez; si ya estaba (edición), respeta
    if (!hidden.value) {
      const today = hoyLocal();
      hidden.value = today;
    }
    if (info && hidden.value) {
      info.textContent = `Firmado el ${fmtFecha(hidden.value)}`;
    }
    this._showUnsaved();
  },

  limpiarFirma() {
    this.data.signaturePad?.clear();
    const info = document.getElementById('firmaFechaInfo');
    if (info) info.textContent = '';
    // Solo limpiamos la fecha si era una nueva remisión (no en edición)
    if (this.data.isNew) {
      const hidden = document.getElementById('recibe_fecha');
      if (hidden) hidden.value = '';
    }
  },

  async cambiarFirma() {
    // Patrón PNC: la URL vieja se memoriza para borrarla del bucket SOLO
    // cuando el usuario presione "Guardar cambios". Si cancela, no se toca.
    if (this.data.remision?.recibe_firma_url) {
      this._firmaViejaABorrar = this.data.remision.recibe_firma_url;
      this.data.remision.recibe_firma_url = null;
    }
    // Re-render con el mismo formato de tarjeta (header RECIBE) en estado vacío.
    document.getElementById('firmaContainer').innerHTML = `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
        <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
          <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">Recibe</span>
        </div>
        <div style="position: relative;">
          <canvas id="firmaCanvas" style="width:100%;height:160px;display:block;"></canvas>
        </div>
        <div style="padding: 0.5rem 1rem 0.75rem; text-align: center; border-top: 1px solid var(--gray-200);">
          <span id="firmaFechaInfo" style="font-size: 0.75rem; color: var(--gray-500);"></span>
          <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary); margin-left: 1rem;" onclick="window.currentView.limpiarFirma()">Limpiar</a>
        </div>
      </div>
    `;
    this._showUnsaved();
    await this._initSignaturePad();
  },

  async _subirFirma(blob) {
    const numSerie = this._numSerie || this.data.remision?.num_serie;
    if (!numSerie) throw new Error('Sin num_serie para subir firma');
    // Reutilizamos el helper canónico que sube al bucket unificado:
    //   {modulo}/{num_serie}/firmas/{filename}
    return await recepcionService.subirFirma(
      blob,
      `firma-recibe-${Date.now()}.png`,
      numSerie,
      'remisiones'
    );
  },

  // Recorta el canvas del SignaturePad a la zona del trazo (bounding box +
  // padding chico de 12px) para que el PNG resultante no tenga whitespace
  // alrededor. Si no hay trazos, devuelve el canvas original sin cambios.
  // Resultado: la firma se ve grande en la impresión sin importar dónde se firmó.
  _trimSignatureCanvas(srcCanvas) {
    const w = srcCanvas.width;
    const h = srcCanvas.height;
    const ctx = srcCanvas.getContext('2d');
    const data = ctx.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    let hasInk = false;
    // Pixel se considera "trazo" si NO es prácticamente blanco (tolerancia 240).
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (data[i] < 240 || data[i + 1] < 240 || data[i + 2] < 240) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          hasInk = true;
        }
      }
    }
    if (!hasInk) return srcCanvas;
    const pad = 12;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);
    const tw = maxX - minX + 1;
    const th = maxY - minY + 1;
    const out = document.createElement('canvas');
    out.width = tw;
    out.height = th;
    const octx = out.getContext('2d');
    octx.fillStyle = 'white';
    octx.fillRect(0, 0, tw, th);
    octx.drawImage(srcCanvas, minX, minY, tw, th, 0, 0, tw, th);
    return out;
  },

  volver() {
    router.navigate('/ventas/remisiones');
  },

  imprimir() {
    if (!this.data.remision?.id) return;
    router.navigate(`/ventas/remisiones/${this.data.remision.id}/print`);
  },

  async completar() {
    if (!this.data.remision?.id) return;
    // Validaciones cliente: fecha, moneda, precio_unitario, recibe_nombre, firma.
    // Cada campo se mapea a su tab/elemento DOM para auto-navegar al primer faltante.
    const fecha = document.getElementById('fecha_remision')?.value;
    const moneda = document.getElementById('moneda')?.value;
    const precioUnit = parseFloat(document.getElementById('precio_unitario')?.value);
    const recibeNombre = document.getElementById('recibe_nombre')?.value?.trim();
    const tienePadConTrazos = this.data.signaturePad && !this.data.signaturePad.isEmpty();
    const tieneFirmaUrl = !!this.data.remision.recibe_firma_url;

    // Lista ordenada (label, fallido?, tab al que pertenece, id del input para focus)
    const checks = [
      { label: 'fecha',                    fallido: !fecha,                              tab: null,      focusId: 'fecha_remision' },
      { label: 'moneda',                   fallido: !moneda,                             tab: 'precio',  focusId: 'moneda' },
      { label: 'precio unitario',          fallido: !precioUnit || precioUnit <= 0,      tab: 'precio',  focusId: 'precio_unitario' },
      { label: 'nombre de quien recibe',   fallido: !recibeNombre,                       tab: 'recibe',  focusId: 'recibe_nombre' },
      { label: 'firma de quien recibe',    fallido: !tienePadConTrazos && !tieneFirmaUrl, tab: 'recibe',  focusId: 'firmaCanvas' }
    ];
    const faltantes = checks.filter(c => c.fallido);
    if (faltantes.length > 0) {
      toast.warning(`Faltan: ${faltantes.map(f => f.label).join(', ')}`);
      // Navegar al tab del PRIMER faltante y enfocar el input.
      const primero = faltantes[0];
      if (primero.tab) {
        this.showTab(primero.tab);
      }
      // Pequeño delay para que el tab se haya activado antes del scroll/focus.
      setTimeout(() => {
        const el = document.getElementById(primero.focusId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') el.focus({ preventScroll: true });
        }
      }, 100);
      return;
    }
    // Si hay cambios pendientes, exigir guardar primero (patrón Recepciones/OT/Checklist/Liberaciones)
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator && indicator.style.display !== 'none') {
      toast.warning('Guarda los cambios antes de completar la remisión');
      return;
    }
    // También bloquear si hay trazos nuevos en el pad sin URL persistida (firma no subida).
    if (this.data.signaturePad && !this.data.signaturePad.isEmpty() && !this.data.remision.recibe_firma_url) {
      toast.warning('Guarda los cambios antes de completar la remisión');
      return;
    }

    // Confirmación: una vez completada queda inmutable
    const ok = await new Promise(resolve => {
      modal.confirm('Completar remisión',
        `<p>Vas a completar la remisión <strong>${esc(this.data.remision.numero_remision || '')}</strong>.</p>
         <p style="margin-top:0.5rem;color:var(--gray-700);">Una vez completada <strong>no podrá editarse</strong>. Solo podrá visualizarse, imprimirse o cancelarse (admin).</p>`,
        () => resolve(true),
        () => resolve(false)
      );
    });
    if (!ok) return;
    try {
      this._showProgress('Completando remisión...');
      await remisionService.completar(this.data.remision.id);
      this._hideProgress();
      toast.success('Remisión completada');
      // Recargar para que isReadonly y los botones reflejen el nuevo estado
      router.navigate(`/ventas/remisiones/${this.data.remision.id}`);
    } catch (err) {
      this._hideProgress();
      toast.error(err.message || 'Error al completar la remisión');
    }
  },

  async cancelar() {
    if (!this.data.remision?.id) return;
    const titulo = this.data.remision.numero_remision || `#${this.data.remision.id}`;
    modal.open('Cancelar remisión',
      `<p>Vas a cancelar la remisión <strong>${esc(titulo)}</strong>.</p>
       <p style="margin-top:0.5rem;color:var(--gray-700);">Quedará marcada como cancelada y solo podrá visualizarse o imprimirse (con watermark).</p>
       <div class="form-group" style="margin-top:1rem;">
         <label>Motivo de cancelación *</label>
         <textarea id="motivoCancelacion" class="form-control" rows="3" placeholder="Ej: error en datos del cliente, precio incorrecto, etc." required></textarea>
       </div>`,
      [
        { text: 'Cerrar', class: 'btn-secondary' },
        {
          text: 'Cancelar remisión', class: 'btn-danger',
          closeOnClick: false,
          onClick: async () => {
            const motivo = document.getElementById('motivoCancelacion')?.value?.trim();
            if (!motivo) { toast.warning('Indica el motivo'); return; }
            try {
              await remisionService.cancelar(this.data.remision.id, motivo);
              modal.close();
              toast.success('Remisión cancelada');
              router.navigate(`/ventas/remisiones/${this.data.remision.id}`);
            } catch (err) {
              toast.error(err.message || 'Error al cancelar');
            }
          }
        }
      ]
    );
  },

  async guardar() {
    try {
      // Validaciones rápidas
      const num_serie = this._numSerie || this.data.remision?.num_serie;
      if (this.data.isNew && !num_serie) { toast.warning('Falta el tractor'); return; }
      const precio_unitario = parseFloat(document.getElementById('precio_unitario').value);
      if (!precio_unitario || precio_unitario <= 0) { toast.warning('Captura el precio unitario'); return; }
      const moneda = document.getElementById('moneda').value;
      if (!moneda) { toast.warning('Selecciona la moneda'); return; }

      this._showProgress('Preparando datos...');

      // Subir firma SOLO si:
      //   1) No hay URL persistida ya (i.e., es nueva remisión, o el usuario hizo
      //      "Cambiar firma" que limpió la URL en memoria), Y
      //   2) El pad tiene trazos.
      // Esto evita re-subir el mismo blob en cada "Guardar cambios" cuando ya
      // existe una firma guardada.
      let recibe_firma_url = this.data.remision?.recibe_firma_url || null;
      if (!recibe_firma_url && this.data.signaturePad && !this.data.signaturePad.isEmpty()) {
        this._showProgress('Subiendo firma...');
        // Recortar el canvas a la zona del trazo (bounding box + padding chico)
        // para que el PNG resultante NO tenga whitespace y se vea grande al imprimir.
        const canvas = document.getElementById('firmaCanvas');
        const trimmed = this._trimSignatureCanvas(canvas);
        const dataURL = trimmed.toDataURL('image/png');
        const blob = await (await fetch(dataURL)).blob();
        recibe_firma_url = await this._subirFirma(blob);
        // Limpiar referencia tras subir — siguiente save no re-sube.
        this.data.signaturePad = null;
      }

      // Vendedor: se captura en el select (persona_id). En readonly no hay select.
      const vendSel = document.getElementById('vendedor_id');
      const vendedor_id = vendSel
        ? (vendSel.value ? parseInt(vendSel.value, 10) : null)
        : (this.data.remision?.vendedor_id || null);

      const payload = {
        num_serie,
        cliente_id: this._cliente_id || this.data.remision?.cliente_id || null,
        vendedor_id,
        fecha_remision: document.getElementById('fecha_remision').value,
        forma_pago: document.getElementById('forma_pago').value,
        cantidad: parseFloat(document.getElementById('cantidad').value) || 1,
        unidad: document.getElementById('unidad').value || 'PZ',
        concepto: document.getElementById('concepto').value || null,
        marca: this.data.tractor?.marca || this.data.remision?.tractor_marca || null,
        modelo: this.data.tractor?.modelo || this.data.remision?.tractor_modelo || null,
        precio_unitario,
        moneda,
        descuento: parseFloat(document.getElementById('descuento').value) || 0,
        iva: parseFloat(document.getElementById('iva').value) || 0,
        total_letras: document.getElementById('total_letras').value || null,
        observaciones: document.getElementById('observaciones').value || null,
        lugar_entrega: document.getElementById('lugar_entrega').value || null,
        condiciones_venta: document.getElementById('condiciones_venta').value || null,
        recibe_nombre: document.getElementById('recibe_nombre').value || null,
        recibe_fecha: document.getElementById('recibe_fecha').value || null,
        recibe_firma_url
      };

      if (this.data.isNew) {
        if (this._checklistId) payload.checklist_documentos_id = parseInt(this._checklistId, 10);
        this._showProgress('Creando remisión...');
        const res = await remisionService.crear(payload);
        await this._borrarFirmaVieja();
        this._hideProgress();
        this._hideUnsaved();
        toast.success('Remisión creada');
        const remId = (res?.data || res)?.id;
        if (remId) router.navigate(`/ventas/remisiones/${remId}`);
        else this.volver();
      } else {
        this._showProgress('Guardando cambios...');
        await remisionService.actualizar(this.data.remision.id, payload);
        await this._borrarFirmaVieja();
        this._hideProgress();
        this._hideUnsaved();
        toast.success('Remisión actualizada');
        router.navigate(`/ventas/remisiones/${this.data.remision.id}`);
      }
    } catch (err) {
      this._hideProgress();
      toast.error(err.message || 'Error al guardar la remisión');
    }
  },

  async _borrarFirmaVieja() {
    if (!this._firmaViejaABorrar) return;
    try {
      this._showProgress('Limpiando firma anterior...');
      await api.post('/files/eliminar', { url: this._firmaViejaABorrar });
    } catch (err) {
      console.warn('No se pudo borrar firma vieja del bucket:', this._firmaViejaABorrar, err.message);
    }
    this._firmaViejaABorrar = null;
  },

  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700);"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  }
};
